'use client';

import type {
  Filter,
  PaginatedProduct,
  SRPFilterAction,
  SRPState,
} from '@dealerportal/common';
import { logger } from '@dealerportal/core';
import { useLocaleSafe } from '@dealerportal/i18n/client';
import {
  currentUrl,
  EncodeFiltersToSearchParams,
  isFlagFilterEnabled,
  MERCHANDISING_FILTERS,
  parseSeoFriendlyFilters,
  remapApplicationName,
  remapBrandName,
  remapCategoryName,
  remapCollectionName,
  remapProductName,
  toCiCMerchandising,
  toTitleCase,
} from '@dealerportal/utilities';
import camelCase from 'es-toolkit/compat/camelCase';
import { useRouter } from 'next/navigation';
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useCiCIntent } from '../Hooks/useCiCIntent';
import {
  DEFAULT_PAGE_SIZE,
  GROUP_REF_SRP_PRIORITY,
  useCiCSearch,
} from '../Hooks/useCiCSearch';
import { useCiCAPI } from '../Hooks/useCicAPI';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// Platform definition:
// MM = Mobile Marketing
// DP = Dealer Portal
export type PlatformType = 'MM' | 'DP';

export interface ICatalogSettings {
  hideOrderSampleButton?: boolean; // Should hide order sample button everywhere
  hideOrderSampleTag?: boolean; // Should hide "Order Sample" tag on SRP
  hideBrandColorSkuTitleInPDP?: boolean; // Should hide brand, color and SKU title on PDP
  hideRequestQuoteButton?: boolean; // Should hide REQUEST QUOTE button on PDP
  hidePricing?: boolean; // Should hide pricing
  hideNewTag?: boolean; // Should hide "NEW" tag on SRP
  groupBySize?: boolean; // Group by size
  hideSizesAvailable?: boolean; // Should hide sizes available on PDP
  hideGetCouponButtonLink?: boolean; // Should hide GET COUPON button/link from SRP/PDP
}

export interface ICatalogSeedData {
  products?: PaginatedProduct[];
  productCount?: number;
  colorCount?: number;
  hasPricing?: boolean;
  tags?: string[];
  filters?: Filter[];
}

export interface ICatalogContext {
  currentPlatform: PlatformType;
  products?: PaginatedProduct[];
  productCount?: number;
  colorCount?: number;
  state: SRPState;
  filtersVisible: boolean;
  setFiltersVisible: Dispatch<SetStateAction<boolean>>;
  updateSort(sort: string): void;
  hasPricing: boolean;
  tags: string[];
  dispatch: React.Dispatch<SRPFilterAction>;
  useCiC: boolean;
  applyNewFilters: (
    filters: Filter[],
    abortSignal?: AbortSignal
  ) => Promise<Filter[]>;
  settings?: ICatalogSettings;
  catalogVersionId?: string;
  catalogId?: number | null;
  loaded: boolean;
  searchWithoutGroupRef?: boolean;
  /** Seeds the context with server-fetched initial state. For CiC, triggers the catalog search. */
  seed: (data: ICatalogSeedData) => void;
  /** Asks the provider to download and initialise the CiC client script. Idempotent. */
  requireCiC: () => void;
}

interface CatalogContextProviderProps {
  children: React.ReactNode;
  initialProducts?: PaginatedProduct[];
  initialState?: SRPState;
  initialHasPricing?: boolean;
  initialTags?: string[];
  initialPage?: number;
  initialProductCount?: number;
  initialColorCount?: number;
  catalogVersionId?: string;
  catalogId?: number | null;
  useCiC: boolean;
  searchWithoutGroupRef?: boolean;
  loadProducts?: boolean;
  platform?: PlatformType;
  reducer?: (
    push: (url: string) => void
  ) => (state: SRPState, action: SRPFilterAction) => SRPState;
  settings?: ICatalogSettings;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Parse URL search parameters and path to extract characteristics filter
 * Converts kebab-case to camelCase and handles the product type from the path
 */
const parseURLAsCharacteristics = (): ICharacteristicsFilter => {
  const { searchParams, pathname } = currentUrl();
  const [_, type] = pathname.split('/p/');

  if (type) {
    const parsedResult = parseSeoFriendlyFilters(type.split('/'));
    const types = parsedResult['Type']?.map((t) => t.replaceAll('-', ''));
    const brands = parsedResult['Brand'];
    const lines = parsedResult['Line'];
    if (types) {
      searchParams.set('type', types.join(','));
    }
    if (brands) {
      searchParams.set('brand', brands.join(','));
    }
    if (lines) {
      searchParams.set('line', lines.join(','));
    }
  }
  searchParams.delete('page');
  searchParams.delete('sort');

  const merchandisings = [
    ...(searchParams.get('tags')?.split(',').filter(Boolean) ?? []).map(
      toCiCMerchandising
    ),
    ...MERCHANDISING_FILTERS.filter(({ name }) =>
      isFlagFilterEnabled(searchParams.getAll(name.toLowerCase()))
    ).map(({ code }) => code),
  ];
  searchParams.delete('tags');
  for (const { name } of MERCHANDISING_FILTERS) {
    searchParams.delete(name.toLowerCase());
  }
  if (merchandisings.length > 0) {
    searchParams.set('merchandisings', merchandisings.join(','));
  }

  return [...searchParams.entries()].reduce(
    (acc: ICharacteristicsFilter, [key, value]: [string, string]) => {
      acc[camelCase(key)] = value.split(',');
      return acc;
    },
    {} as ICharacteristicsFilter
  );
};

/**
 * Default reducer that returns the state unchanged
 */
const defaultReducer = (state: SRPState) => state;

// ============================================================================
// CONTEXT IMPLEMENTATION
// ============================================================================

export const useCatalogContext = () => {
  const context = useContext(CatalogContext);
  if (!context) {
    throw new Error(
      'useCatalogContext must be used within a CatalogContextProvider'
    );
  }
  return context;
};

/**
 * Declares that the calling component needs the CiC client script. The provider downloads it on
 * the first request, so pages without a CiC consumer never pay for it.
 */
export const useRequireCiC = (enabled = true) => {
  const { requireCiC } = useCatalogContext();
  useEffect(() => {
    if (enabled) requireCiC();
  }, [enabled, requireCiC]);
};

export const CatalogContext = createContext<ICatalogContext | null>(null);

export function CatalogContextProvider({
  children,
  initialState,
  initialProducts,
  initialTags,
  initialHasPricing,
  initialPage = 1,
  initialProductCount,
  initialColorCount,
  useCiC,
  loadProducts = true,
  platform = 'DP',
  catalogVersionId,
  catalogId,
  reducer,
  settings,
  searchWithoutGroupRef = false,
}: CatalogContextProviderProps) {
  console.debug('CatalogContext initialized');
  const router = useRouter();
  const resolvedReducer = useMemo(
    () => (reducer ? reducer(router.push) : defaultReducer),
    [reducer, router]
  );
  const [state, dispatch] = useReducer(resolvedReducer, {
    ...(initialState ?? { filters: [], loading: true }),
    needsHydration: false,
  });
  const [products, setProducts] = useState<PaginatedProduct[] | undefined>(
    initialProducts
  );
  const [productCount, setProductCount] = useState<number | undefined>(
    initialProductCount
  );
  const [colorCount, setColorCount] = useState<number | undefined>(
    initialColorCount
  );

  const [hasPricing, setHasPricing] = useState(initialHasPricing ?? false);
  const [tags, setTags] = useState<string[]>(initialTags ?? []);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [selectedSort, setSelectedSort] = useState('');
  // pendingLoad drives the CiC search trigger. Initialized from loadProducts prop
  // so existing callers with loadProducts={true} continue to auto-load.
  const [pendingLoad, setPendingLoad] = useState<{
    initialPage: number;
  } | null>((loadProducts ?? true) ? { initialPage: initialPage ?? 1 } : null);

  const [cicRequested, setCiCRequested] = useState(loadProducts ?? true);
  const requireCiC = useCallback(() => setCiCRequested(true), []);
  useCiCIntent(useCiC && !cicRequested, requireCiC);

  const locale = useLocaleSafe();
  const { loaded } = useCiCAPI({
    loadScript: useCiC && cicRequested,
    locale,
  });
  const { search, getFilters } = useCiCSearch();

  const updateSort = useCallback(
    (sort: string) => {
      if (selectedSort !== sort) setSelectedSort(sort);
      dispatch?.({
        type: 'UPDATE_SORT',
        value: sort,
      });
    },
    [selectedSort]
  );

  const seed = useCallback(
    (data: ICatalogSeedData) => {
      if (data.products !== undefined) setProducts(data.products);
      if (data.productCount !== undefined) setProductCount(data.productCount);
      if (data.colorCount !== undefined) setColorCount(data.colorCount);
      if (data.hasPricing !== undefined) setHasPricing(data.hasPricing);
      if (data.tags !== undefined) setTags(data.tags);
      if (data.filters !== undefined)
        dispatch({ type: 'RESET', filters: data.filters });
      if (useCiC) {
        setCiCRequested(true);
        // When no products are provided, trigger the catalog search
        if (data.products === undefined) {
          const page = Number(currentUrl().searchParams.get('page') || 1);
          setPendingLoad({ initialPage: page });
        }
      }
    },
    [dispatch, useCiC]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs only on pending load
  useEffect(() => {
    logger
      .withMetadata({ pendingLoad, loaded, catalogVersionId })
      .debug('CatalogContext useEffect triggered');
    // Wait for CiC script to load and a pending load to be requested
    if (!loaded || !pendingLoad || !catalogVersionId) return;
    const { initialPage: page } = pendingLoad;
    const characteristics = parseURLAsCharacteristics();
    const searchText = characteristics['q']?.[0] ?? '';
    delete characteristics['q'];
    search({
      catalogVersionId,
      initialPage: page,
      characteristics,
      excludeFilters: false,
      excludeVariants: true,
      pageSize: DEFAULT_PAGE_SIZE,
      groupRefs: searchWithoutGroupRef ? undefined : [GROUP_REF_SRP_PRIORITY],
      searchText,
    })
      .then((result) => {
        // Line only applies within a brand
        if (!characteristics['brand']) {
          result.filters = result.filters.filter(
            (filter) => filter.code !== 'line'
          );
        }
        setProducts(result.products as PaginatedProduct[]);
        setColorCount(result.colorCount);
        setProductCount(result.productCount);
        dispatch({
          type: 'RESET',
          filters: result.filters as Filter[],
        });
      })
      .catch((error) => {
        logger.withError(error).error('Error loading products:');
        setProducts([]);
        setProductCount(0);
        setColorCount(0);
        dispatch({
          type: 'RESET',
          filters: [],
        });
      })
      .finally(() => setPendingLoad(null));
  }, [loaded, pendingLoad]);

  // ============================================================================
  // FILTER SERVICE FUNCTIONS
  // ============================================================================

  /**
   * Fetch updated filter counts from the server based on the current filters
   * @param filters the current filters
   * @param abortSignal (optional) an abort signal to cancel the request
   * @returns new filters from the server based on the current enabled values
   */
  const fetchNewFilterCounts = async (
    filters: Filter[],
    abortSignal?: AbortSignal
  ): Promise<Filter[]> => {
    const encoded = EncodeFiltersToSearchParams(null, filters);
    const filterRequestBody = {
      FilterQuery: encoded.toString(),
    };

    try {
      const response = await fetch('/api/getFilters', {
        method: 'POST',
        body: JSON.stringify(filterRequestBody),
        signal: abortSignal,
        headers: {
          'Content-Type': 'application/json',
        },
        next: {
          revalidate: 0,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch filters: ${response.statusText}`);
      }

      const result = await response.json();
      return result.Filters;
    } catch (error) {
      logger.withError(error).error('Error fetching new filter counts:');
      throw error;
    }
  };

  /**
   * Process type filter to remove dashes from enabled values
   */
  const processTypeFilter = (filter: Filter): Filter => {
    if (filter.code === 'type') {
      return {
        ...filter,
        enabledValues: filter.enabledValues.map((value) =>
          value.replace(/-/g, '')
        ),
      };
    }
    return filter;
  };

  /**
   * Convert filters to characteristics object for CiC API
   */
  const filtersToCharacteristics = (
    filters: Filter[]
  ): ICharacteristicsFilter => {
    return filters
      .filter((filter) => filter.enabledValues.length > 0)
      .reduce((acc, filter) => {
        acc[filter.code ?? ''] = filter.enabledValues;
        return acc;
      }, {} as ICharacteristicsFilter);
  };

  /**
   * Move the samples and visualizable filters into CiC merchandising codes
   */
  const addMerchandisingCharacteristics = (
    characteristics: ICharacteristicsFilter,
    filters: Filter[]
  ): void => {
    for (const { name, code } of MERCHANDISING_FILTERS) {
      const key = name.toLowerCase();
      const filter = filters.find((f) => f.code === key);
      if (isFlagFilterEnabled(filter?.enabledValues)) {
        characteristics['merchandisings'] = [
          ...(characteristics['merchandisings'] ?? []),
          code,
        ];
      }
      delete characteristics[key];
    }
  };

  /**
   * Convert tags to CiC merchandising codes
   */
  const convertTagsToMerchandisings = (
    characteristics: ICharacteristicsFilter
  ): void => {
    if (characteristics['tags']) {
      const tags = characteristics['tags'];
      delete characteristics['tags'];
      characteristics['merchandisings'] = [
        ...(characteristics['merchandisings'] ?? []),
        ...tags.map(toCiCMerchandising),
      ];
    }
  };

  /**
   * Merge filters with multi-select handling
   */
  const mergeFilters = (
    oldFilters: Filter[],
    newFilters: Filter[]
  ): Filter[] => {
    const multiSelectFilterCodes = new Set(['type', 'line', 'collection']);

    const nonMultiSelectFilters = newFilters.filter(
      (filter) => !multiSelectFilterCodes.has(filter.code ?? '')
    );

    const originalMultiSelectFilters = oldFilters.filter((filter) =>
      multiSelectFilterCodes.has(filter.code ?? '')
    );

    return [...nonMultiSelectFilters, ...originalMultiSelectFilters];
  };

  /**
   * Fetch updated filter counts from CiC API based on the current filters
   * @param catalogVersionId the catalog version ID
   * @param oldFilters the current filters
   * @returns new filters from CiC API based on the current enabled values
   */
  const fetchNewFilterCountsCIC = async (
    catalogVersionId: string,
    oldFilters: Filter[]
  ): Promise<Filter[]> => {
    try {
      const processedFilters = oldFilters.map(processTypeFilter);

      const characteristics = filtersToCharacteristics(processedFilters);

      addMerchandisingCharacteristics(characteristics, processedFilters);
      convertTagsToMerchandisings(characteristics);

      // Fetch filters with brand constraints
      const { filters: filtersWithoutBrand } = await getFilters({
        catalogVersionId,
        characteristics,
      });
      // Remove brand from constrained filters and merge with unconstrained brand filter
      const filtersExcludingBrand = filtersWithoutBrand.filter(
        (filter) => filter.code !== 'brand'
      );

      // Fetch filters without brand to get full brand list
      const characteristicsWithoutBrand = { ...characteristics };
      delete characteristicsWithoutBrand['brand'];

      const { filters: filtersWithBrand } = await getFilters({
        catalogVersionId,
        characteristics: characteristicsWithoutBrand,
      });

      const brandFilter = filtersWithBrand.find(
        (filter) => filter.code === 'brand'
      );

      return mergeFilters(oldFilters, [
        ...filtersExcludingBrand,
        ...(brandFilter ? [brandFilter] : []),
      ]);
    } catch (error) {
      logger.withError(error).error('Error fetching CiC filter counts:');
      throw error;
    }
  };

  /**
   * Factory function to create the appropriate filter fetcher based on CiC usage
   * @param useCiC whether to use CiC API
   * @param catalogVersionId the catalog version ID (required when useCiC is true)
   * @returns filter fetcher function
   */
  function createFilterFetcher(useCiC: boolean, catalogVersionId?: string) {
    return async (
      filters: Filter[],
      abortSignal?: AbortSignal
    ): Promise<Filter[]> => {
      if (useCiC) {
        if (!catalogVersionId) {
          throw new Error('catalogVersionId is required when using CiC');
        }
        return fetchNewFilterCountsCIC(catalogVersionId, filters);
      }
      return fetchNewFilterCounts(filters, abortSignal);
    };
  }

  useEffect(() => {
    window.catalogConfig = {
      ...settings,
      currentPlatform: platform,
    };
  }, [platform, settings]);

  useEffect(() => {
    const getDisplayNameForFilter = (
      filterName: string,
      displayValue: string | undefined,
      value: string
    ) => {
      switch (filterName) {
        case 'Brand':
          return remapBrandName(displayValue ?? value);

        case 'Line':
          return remapProductName(displayValue ?? value);

        case 'Fiber Brand':
          return displayValue ?? value;

        case 'Collection':
          return remapCollectionName(displayValue ?? value);

        case 'Application':
          return remapApplicationName(displayValue ?? value);

        case 'Category':
          return remapCategoryName(displayValue ?? value);

        default:
          return toTitleCase(displayValue ?? value);
      }
    };

    if (!useCiC) {
      // Remap display name of a filter value for non-CiC filters
      for (const filter of state.filters) {
        filter.displayNames = Object.fromEntries(
          Object.keys(filter.potentialValues).map((key) => [
            key,
            getDisplayNameForFilter(filter.name, filter.displayNames[key], key),
          ])
        );
      }
    }
  }, [state, useCiC]);

  return (
    <CatalogContext.Provider
      value={{
        products,
        state,
        filtersVisible,
        setFiltersVisible,
        updateSort,
        hasPricing,
        tags,
        dispatch,
        useCiC,
        applyNewFilters: createFilterFetcher(useCiC, catalogVersionId),
        currentPlatform: platform,
        productCount,
        colorCount,
        settings,
        catalogVersionId,
        catalogId,
        loaded,
        searchWithoutGroupRef,
        seed,
        requireCiC,
      }}
    >
      {children}
    </CatalogContext.Provider>
  );
}
